import { useEffect, useRef, useState } from "react";

import { css } from "@emotion/react";

import { AudioInputLevel } from "components/interview/AudioInputLevel";
import { Button } from "components/shared/library/Button";
import { Disclaimer } from "components/shared/library/Disclaimer";
import { DropdownDeprecated } from "components/shared/library/DropdownDeprecated";
import { Typography } from "components/shared/library/Typography";

import { useInterview } from "contexts/InterviewContext";

import {
  getAudioStreamFromMediaSinkId,
  getMediaDevices,
  getVideoStreamFromMediaSinkId,
  useStreamVolume,
} from "utils/Stream";

import { purple, uiGray } from "constants/colors";

const style = {
  wrapper: css({
    display: "flex",
    flexDirection: "column",
    gap: 16,
    width: "-webkit-fill-available",
  }),
  dropdowns: {
    wrapper: css({ width: "100%" }),
    text: css({
      overflow: "hidden",
      textOverflow: "ellipsis",
      display: "-webkit-box",
      WebkitBoxOrient: "vertical",
      WebkitLineClamp: 1,
    }),
  },
  speaker: css({ display: "flex", alignItems: "end", gap: 16, width: "100%" }),
  microphone: css({
    display: "flex",
    flexDirection: "column",
    gap: 16,
    width: "100%",
  }),
  videoSection: css({
    display: "flex",
    gap: 16,
    alignItems: "center",
  }),
  videoContainer: css({
    borderRadius: 16,
    overflow: "hidden",
    height: 96,
    background: purple[100],
    flexShrink: 0,
  }),
  videoText: css({
    display: "flex",
    flexDirection: "column",
    gap: 4,
  }),
};

type Props = {
  onAudioStreamChange: (audioStream?: MediaStream) => void;
  onVideoStreamChange: (videoStream?: MediaStream) => void;
  audioStream?: MediaStream;
  videoStream?: MediaStream;
  onSinkIdChange: (sinkId?: string) => void;
  sinkId?: string;
};
export const AudioSettings = ({
  onAudioStreamChange,
  onVideoStreamChange,
  audioStream,
  videoStream,
  onSinkIdChange: setSelectedAudioOutId,
  sinkId: selectedAudioOutId,
}: Props) => {
  const audioRef = useRef<HTMLMediaElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [audioInputs, setAudioInputs] = useState<MediaDeviceInfo[]>([]);
  const [videoInputs, setVideoInputs] = useState<MediaDeviceInfo[]>([]);
  const [audioOutputs, setAudioOutputs] = useState<MediaDeviceInfo[]>([]);
  const [selectedAudioInId, setSelectedAudioInId] = useState("");
  const [selectedVideoInId, setSelectedVideoInId] = useState("");
  const [permissionStatus, setPermissionStatus] = useState<
    "loading" | "prompted" | "granted" | "denied"
  >("loading");

  const { isVideoEnabled } = useInterview();
  const volume = useStreamVolume(audioStream);

  useEffect(() => {
    const getDevices = async () => {
      const {
        permissionStatus,
        audioInputs: audioIns,
        audioOutputs: audioOuts,
        videoInputs: videoIns,
      } = await getMediaDevices(isVideoEnabled);
      setAudioInputs(audioIns);
      setAudioOutputs(audioOuts);
      setVideoInputs(videoIns);
      setSelectedAudioInId(audioIns[0]?.deviceId);
      setSelectedAudioOutId(audioOuts[0]?.deviceId);
      setSelectedVideoInId(videoIns[0]?.deviceId);
      setPermissionStatus(permissionStatus);
    };

    getDevices();
  }, [setSelectedAudioOutId, isVideoEnabled]);

  // updating speaker
  useEffect(() => {
    // change the audio output for the speaker test if supported by browser
    // HTMLMediaElement has the prop setSinkId typed, but this method actually only exists on supporting browsers
    if (audioRef.current?.setSinkId && selectedAudioOutId) {
      audioRef.current.setSinkId(selectedAudioOutId);
    }
  }, [selectedAudioOutId]);

  // When updating microphone - we capture the new audio audioStream
  useEffect(() => {
    const getStream = async () => {
      const newStream = await getAudioStreamFromMediaSinkId(selectedAudioInId);
      onAudioStreamChange(newStream);
    };

    if (selectedAudioInId) getStream();
  }, [selectedAudioInId, onAudioStreamChange]);

  // When updating video input - we capture the new video audioStream
  useEffect(() => {
    const getStream = async () => {
      const newStream = await getVideoStreamFromMediaSinkId(selectedVideoInId);
      onVideoStreamChange(newStream);
    };

    if (selectedVideoInId) getStream();
  }, [selectedVideoInId, onVideoStreamChange]);

  // If there is a video stream, we should play it
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.srcObject = videoStream || null;
    }
  }, [videoStream]);

  return (
    <>
      <div css={style.wrapper}>
        <Typography size="M" weight="bold">
          Audio settings
        </Typography>
        {selectedAudioInId && (
          <div css={style.microphone}>
            <DropdownDeprecated
              label={"Microphone"}
              onChange={(value) => {
                if (value) setSelectedAudioInId(value);
              }}
              options={audioInputs.map(({ deviceId, label }) => ({
                label,
                name: deviceId,
              }))}
              defaultValue={selectedAudioInId}
              customCss={style.dropdowns}
            ></DropdownDeprecated>
            <AudioInputLevel volumeInDb={volume || 0} />
          </div>
        )}

        <div css={style.speaker}>
          {selectedAudioOutId ? (
            <DropdownDeprecated
              label={"Speaker"}
              onChange={(value) => {
                if (value) setSelectedAudioOutId(value);
              }}
              options={audioOutputs.map(({ deviceId, label }) => ({
                label,
                name: deviceId,
              }))}
              defaultValue={selectedAudioOutId}
              customCss={style.dropdowns}
            ></DropdownDeprecated>
          ) : (
            // Safari doesn't return the output devices, so we can't change the speaker
            // so we just show the default speaker
            <DropdownDeprecated
              key={"safari"}
              label={"Speaker"}
              onChange={() => undefined}
              options={[
                {
                  label: "System default audio output",
                  name: "default-device-id",
                },
              ]}
              defaultValue={"default-device-id"}
              customCss={style.dropdowns}
            ></DropdownDeprecated>
          )}

          <Button
            onClick={() => {
              if (audioRef.current) audioRef.current.play();
            }}
            color="black"
            background="outlined"
          >
            Test speaker
          </Button>
        </div>
      </div>
      {isVideoEnabled && (
        <div css={style.wrapper}>
          <Typography size="M" weight="bold">
            Video settings
          </Typography>
          {permissionStatus === "loading" && (
            <Disclaimer title="Loading..." type="info" />
          )}
          {permissionStatus === "denied" && (
            <Disclaimer
              title="This interview requires your camera to be enabled"
              type="error"
            />
          )}
          {selectedVideoInId && (
            <div css={style.videoSection}>
              <div css={style.videoContainer}>
                <video
                  ref={videoRef}
                  autoPlay
                  height={96}
                  width={128}
                  aria-label="Video preview"
                  playsInline
                />
              </div>
              <div css={style.videoText}>
                <Typography size="XS" weight="medium">
                  Smile you&apos;re on camera!
                </Typography>
                <Typography size="XS" weight="medium" color={uiGray[70]}>
                  Center yourself in the frame and make sure your interviewer
                  can see you clearly.
                </Typography>
              </div>
            </div>
          )}
          {selectedVideoInId && (
            <div css={style.speaker}>
              <DropdownDeprecated
                label={"Camera"}
                displayLabel={false}
                onChange={(value) => {
                  if (value) setSelectedVideoInId(value);
                }}
                options={videoInputs.map(({ deviceId, label }) => ({
                  label,
                  name: deviceId,
                }))}
                defaultValue={selectedVideoInId}
                customCss={style.dropdowns}
              ></DropdownDeprecated>
            </div>
          )}
        </div>
      )}

      <audio src={"/images/file_example_WAV_1MG.wav"} ref={audioRef} />
    </>
  );
};
